import type { Labelled } from './common.js';

/**
 * الفوترة الدورية.
 *
 * عصب دخل شركات الخدمات هو الأتعاب الشهرية المتكررة، وإصدارها يدويًا في
 * أول كل شهر أكثر عمل تكرارًا في حياة العميل. القالب هنا يُصدر نفسه.
 */

export const RECURRENCE_CYCLES = ['MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'YEARLY'] as const;
export type RecurrenceCycle = (typeof RECURRENCE_CYCLES)[number];

export interface RecurrenceCycleDef extends Labelled<RecurrenceCycle> {
  months: number;
}

export const RECURRENCE_CYCLE_DEFS: readonly RecurrenceCycleDef[] = [
  { key: 'MONTHLY', label: 'شهري', kind: 'info', months: 1 },
  { key: 'QUARTERLY', label: 'ربع سنوي', kind: 'info', months: 3 },
  { key: 'SEMIANNUAL', label: 'نصف سنوي', kind: 'info', months: 6 },
  { key: 'YEARLY', label: 'سنوي', kind: 'info', months: 12 },
] as const;

export const RECURRENCE_CYCLE_MAP = Object.fromEntries(
  RECURRENCE_CYCLE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<RecurrenceCycle, RecurrenceCycleDef>>;

export const RECURRING_STATUSES = ['ACTIVE', 'PAUSED', 'ENDED'] as const;
export type RecurringStatus = (typeof RECURRING_STATUSES)[number];

export const RECURRING_STATUS_DEFS: readonly Labelled<RecurringStatus>[] = [
  { key: 'ACTIVE', label: 'فعّال', kind: 'success' },
  { key: 'PAUSED', label: 'موقوف مؤقتًا', kind: 'warning' },
  { key: 'ENDED', label: 'منتهٍ', kind: 'neutral' },
] as const;

export const RECURRING_STATUS_MAP = Object.fromEntries(
  RECURRING_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<RecurringStatus, Labelled<RecurringStatus>>>;

/* ==========================================================================
   حساب الدورات
   ========================================================================== */

/** يوم بلا وقت بتوقيت UTC — يطابق أعمدة date في قاعدة البيانات. */
export function utcDay(value: string | Date): Date {
  if (value instanceof Date) {
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
  }
  return new Date(`${value.slice(0, 10)}T00:00:00.000Z`);
}

export function dayString(value: Date): string {
  return value.toISOString().slice(0, 10);
}

/** عدد أيام شهر معيّن. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/**
 * تاريخ الإصدار في شهر معيّن.
 *
 * اليوم المطلوب يُقصر على آخر أيام الشهر القصير: اشتراك يُصدر في الحادي
 * والثلاثين يصير في فبراير الثامن والعشرين لا الثالث من مارس. الزيادة
 * الصامتة كانت ستُخرج فاتورة فبراير في شهر مارس فتختل المطابقة الشهرية.
 */
export function issueDateIn(year: number, month: number, dayOfMonth: number): Date {
  const day = Math.min(Math.max(dayOfMonth, 1), daysInMonth(year, month));
  return new Date(Date.UTC(year, month, day));
}

/**
 * أول دورة لاشتراك جديد.
 *
 * تبدأ من تاريخ البدء نفسه لا من يوم الإصدار في شهره: اشتراك يُنشأ يوم
 * العاشر بيوم إصدار الأول لو حُسبت دورته الأولى من الأول لأُصدرت فاتورة
 * عن أيام سبقت الاشتراك. مشتركة بين اشتراكات العملاء والموردين.
 */
export function firstPeriodStart(start: Date, dayOfMonth: number): Date {
  return issueDateIn(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    Math.max(dayOfMonth, start.getUTCDate()),
  );
}

/**
 * موعد الدورة التالية عند الاستئناف بعد إيقاف مؤقت، أو null إن لم يتغيّر.
 *
 * الاستئناف لا يُصدر ما فات: الإيقاف قرار بألّا يُفوتَر في تلك المدة.
 * فإن كان الموعد المحفوظ قد مضى يُنقل إلى يوم الإصدار في الشهر الجاري.
 */
export function resumeRunAt(nextRunAt: Date, dayOfMonth: number, today: Date): Date | null {
  const day = utcDay(today);
  return nextRunAt < day ? issueDateIn(day.getUTCFullYear(), day.getUTCMonth(), dayOfMonth) : null;
}

/**
 * الدورة التالية بعد دورة بدأت في تاريخ معيّن.
 *
 * تُحسب من بداية الدورة الحالية لا من "اليوم": البناء على اللحظة الراهنة
 * يجعل تأخّر تشغيل المجدول يومًا واحدًا يزحزح كل الدورات التالية معه.
 */
export function nextPeriodStart(
  currentStart: Date,
  cycle: RecurrenceCycle,
  dayOfMonth: number,
): Date {
  const months = RECURRENCE_CYCLE_MAP[cycle].months;
  const y = currentStart.getUTCFullYear();
  const m = currentStart.getUTCMonth() + months;
  return issueDateIn(y + Math.floor(m / 12), ((m % 12) + 12) % 12, dayOfMonth);
}

/** آخر يوم في الدورة التي تبدأ في هذا التاريخ. */
export function periodEndOf(start: Date, cycle: RecurrenceCycle, dayOfMonth: number): Date {
  const next = nextPeriodStart(start, cycle, dayOfMonth);
  return new Date(next.getTime() - 86_400_000);
}

/**
 * الدورات المستحقة حتى تاريخ معيّن.
 *
 * تُعيد كل دورة فات موعدها ولم تُصدَر بعد، لا الأخيرة وحدها: انقطاع الخادم
 * شهرين لا يُسقط فاتورتَي شهرين من حق الشركة.
 *
 * والحد الأقصى ليس تجميلًا: اشتراك يُنشأ بتاريخ بدء قديم بالخطأ كان
 * سيُخرج عشرات الفواتير دفعة واحدة إلى عميل حقيقي.
 */
export const MAX_CATCHUP_PERIODS = 12;

export function duePeriods(
  nextRunAt: Date,
  cycle: RecurrenceCycle,
  dayOfMonth: number,
  until: Date,
  endDate: Date | null,
  limit: number = MAX_CATCHUP_PERIODS,
): Date[] {
  const out: Date[] = [];
  let cursor = utcDay(nextRunAt);
  const stop = utcDay(until);

  while (cursor <= stop && out.length < limit) {
    if (endDate && cursor > utcDay(endDate)) break;
    out.push(cursor);
    cursor = nextPeriodStart(cursor, cycle, dayOfMonth);
  }

  return out;
}

/** عنوان الفاتورة المُصدَرة، ليُميّز شهرها على المستند. */
const MONTHS = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
] as const;

export function periodTitle(title: string, start: Date, cycle: RecurrenceCycle): string {
  const month = MONTHS[start.getUTCMonth()];
  const year = start.getUTCFullYear();

  if (cycle === 'MONTHLY') return `${title} — ${month} ${year}`;

  const end = periodEndOf(start, cycle, start.getUTCDate());
  const endMonth = MONTHS[end.getUTCMonth()];
  const endYear = end.getUTCFullYear();

  return year === endYear
    ? `${title} — ${month} إلى ${endMonth} ${year}`
    : `${title} — ${month} ${year} إلى ${endMonth} ${endYear}`;
}
